import { CONFIG } from "../config.ts";
import { clamp, randRange, TAU } from "../math.ts";
import type { BeingKind } from "../sprites.ts";

// Being tiers double as both poop value AND threat level. A being whose threat
// exceeds the player's realm becomes a predator that hunts you.
const TIER: Record<BeingKind, number> = {
  fly: 1,
  frog: 2,
  dog: 3,
  cow: 4,
  human: 5,
  monk: 6,
  demon: 7, // wrathful guardian — the apex predator of the later realms
};

export interface PlayerRef {
  x: number;
  y: number;
  stage: number;
  radius: number;
}

export class Being {
  x: number;
  y: number;
  kind: BeingKind;
  radius: number;
  tier: number;
  hunting = false; // currently chasing the player (for menacing visuals)
  private vx: number;
  private vy: number;
  private wanderTimer = 0;
  private poopTimer: number;
  animT: number;
  faceX = 1;
  faceY = 0;

  constructor(x: number, y: number, kind: BeingKind, rng: () => number) {
    this.x = x;
    this.y = y;
    this.kind = kind;
    this.tier = TIER[kind];
    this.radius = 16 + this.tier * 4.5;
    this.animT = rng() * 10;
    const a = rng() * TAU;
    const s = randRange(rng, CONFIG.beings.wanderSpeedMin, CONFIG.beings.wanderSpeedMax);
    this.vx = Math.cos(a) * s;
    this.vy = Math.sin(a) * s;
    this.poopTimer = randRange(rng, CONFIG.beings.poopIntervalMin, CONFIG.beings.poopIntervalMax);
  }

  /** A being hunts the player when its threat outranks the player's realm. */
  isPredatorTo(stage: number): boolean {
    return this.tier > stage + CONFIG.predators.safeMargin;
  }

  private chaseSpeed(stage: number): number {
    const p = CONFIG.predators;
    return p.baseChaseSpeed + p.chaseSpeedPerThreat * this.tier + p.chaseSpeedPerStage * stage;
  }

  /**
   * Advance. Returns true when it wants to drop a poop this frame; the world
   * reads `.x/.y/.tier` to place it.
   */
  update(dt: number, rng: () => number, player: PlayerRef, allowHunt = true): boolean {
    this.animT += dt;

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const distToPlayer = Math.hypot(dx, dy);
    const predator = this.isPredatorTo(player.stage);
    this.hunting = allowHunt && predator && distToPlayer < CONFIG.predators.senseRadius;

    if (this.hunting) {
      // Home in on the player.
      const sp = this.chaseSpeed(player.stage);
      const inv = 1 / (distToPlayer || 1);
      this.vx = dx * inv * sp;
      this.vy = dy * inv * sp;
    } else {
      // Idle wander with periodic re-aim.
      this.wanderTimer -= dt;
      if (this.wanderTimer <= 0) {
        this.wanderTimer = randRange(rng, 1.5, 4);
        const a = rng() * TAU;
        const s = randRange(rng, CONFIG.beings.wanderSpeedMin, CONFIG.beings.wanderSpeedMax);
        this.vx = Math.cos(a) * s;
        this.vy = Math.sin(a) * s;
      }
    }

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    const r = this.radius;
    if (!this.hunting) {
      if (this.x < r || this.x > CONFIG.world.width - r) this.vx *= -1;
      if (this.y < r || this.y > CONFIG.world.height - r) this.vy *= -1;
    }
    this.x = clamp(this.x, r, CONFIG.world.width - r);
    this.y = clamp(this.y, r, CONFIG.world.height - r);

    const sp = Math.hypot(this.vx, this.vy) || 1;
    this.faceX = this.vx / sp;
    this.faceY = this.vy / sp;

    this.poopTimer -= dt;
    if (this.poopTimer <= 0) {
      this.poopTimer = randRange(rng, CONFIG.beings.poopIntervalMin, CONFIG.beings.poopIntervalMax);
      return true;
    }
    return false;
  }
}
